import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertCircle } from 'lucide-react';
import AuthService from '../../application/services/AuthService';
import './AuthCallbackPage.css';

type Status = 'exchanging' | 'error';

/**
 * New /auth/callback route (D9) — the web OAuth2/PKCE token-exchange
 * endpoint for the community_web_app client. Separate from, and does not
 * touch, the existing /auth/login mobile bridge.
 */
const AuthCallbackPage: React.FC = () => {
  const navigate = useNavigate();
  const [status, setStatus] = useState<Status>('exchanging');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const hasRun = useRef(false);

  useEffect(() => {
    // The authorization code is single-use — guard against StrictMode's
    // intentional double-invoke of effects in development.
    if (hasRun.current) return;
    hasRun.current = true;

    const params = new URLSearchParams(window.location.search);
    const oauthError = params.get('error');
    const code = params.get('code');
    const state = params.get('state');

    if (oauthError) {
      setStatus('error');
      setErrorMessage(params.get('error_description') || oauthError);
      return;
    }

    if (!code || !state) {
      setStatus('error');
      setErrorMessage('Missing authorization code.');
      return;
    }

    AuthService.completeLogin(code, state)
      .then((returnTo) => navigate(returnTo, { replace: true }))
      .catch((err: unknown) => {
        setStatus('error');
        setErrorMessage(err instanceof Error ? err.message : 'Sign-in failed.');
      });
  }, [navigate]);

  if (status === 'error') {
    return (
      <div className="auth-callback-root">
        <div className="acb-card acb-card-error">
          <AlertCircle size={28} />
          <h1>Sign-in didn't go through</h1>
          <p>{errorMessage}</p>
          <a href="/my-community" className="acb-retry">Try again</a>
        </div>
      </div>
    );
  }

  return (
    <div className="auth-callback-root">
      <div className="acb-card">
        <span className="acb-spinner" />
        <p>Finishing sign-in…</p>
      </div>
    </div>
  );
};

export default AuthCallbackPage;
